"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"

// Define video path constant
const MAIN_VIDEO_PATH = "/newusevid.mp4"

const RecommendationModal = ({ isOpen, onClose, onConfirm, isLoading, progressStatus = "", roleId }) => {
  const navigate = useNavigate()
  
  // --- Helper function to determine stage, message, and percentage ---
  const getProgressInfo = () => {
    if (!isLoading) return { message: "", stage: 'Idle', percentage: 0 };

    if (progressStatus.startsWith("✓")) {
        return { message: "Recommendations ready! Preparing view...", stage: 'Complete', percentage: 100 };
    }
    if (progressStatus.includes("Error:")) {
        // Extract error message after "Error:"
        const errorMessage = progressStatus.split('Error:')[1]?.trim() || "An unknown error occurred.";
        return { message: `Error: ${errorMessage}`, stage: 'Error', percentage: 0 }; // Reset percentage on error
    }
    if (progressStatus.includes("Saving")) {
        return { message: "Finalizing your personalized recommendations...", stage: 'Saving', percentage: 90 };
    }
    if (progressStatus.includes("Evaluating") || progressStatus.includes("Phase 2")) {
        const countMatch = progressStatus.match(/Evaluating (\d+)/);
        const count = countMatch ? countMatch[1] : 'top';
        return { message: `Ranking ${count} potential matches using AI...`, stage: 'Ranking', percentage: 65 };
    }
    if (progressStatus.includes("Phase 1")) {
        return { message: "Searching candidate pool based on requirements...", stage: 'Searching', percentage: 35 };
    }
    if (progressStatus.includes("Analyzing job requirements") || progressStatus.includes("Using manual requirements")) {
        return { message: "Understanding your role requirements...", stage: 'Analyzing', percentage: 15 };
    }
    // Default / Initial state before specific statuses are set
    return { message: "Initializing candidate search...", stage: 'Initializing', percentage: 5 };
  };
  // --- End Helper function ---

  // Get current progress info
  const progressInfo = getProgressInfo();
  
  // Check if we should redirect based on progress status (stage)
  useEffect(() => {
    // Only check when modal is open and loading
    if (!isOpen || !isLoading) return;

    // Check for completion stage
    if (progressInfo.stage === 'Complete') {
      console.log("Found completion stage, preparing redirect.");

      // Simple delay then redirect
      const timer = setTimeout(() => {
        console.log("Navigation timer fired, redirecting to recommendation view");
        onClose();
        if (roleId) {
          // Use navigate for smoother SPA transition if possible
          navigate(`/browse-candidates-recommendation/${roleId}`); // SPA navigation
        }
      }, 2000); // Keep delay for user to see completion message

      return () => clearTimeout(timer);
    }
  }, [progressInfo.stage, isOpen, isLoading, roleId, onClose, navigate]);
  
  // If modal is closed, don't render anything
  if (!isOpen) return null;
  
  // Handle the browse button click
  const handleBrowseClick = () => {
    onClose();
    navigate('/browse-candidates');
  };
  
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-0 w-full max-w-3xl shadow-2xl transform transition-all overflow-hidden max-h-[90vh] overflow-y-auto">
        {isLoading ? (
          // ------------------------------
          // Loading State – keep as is
          // ------------------------------
          <div className="flex flex-col">
            {/* Header */}
            <div className="bg-white p-5 border-b">
              <h3 className="text-xl font-medium text-gray-900">Finding Your Perfect Candidates</h3>
              <p className="text-gray-500 mt-1">Our AI is matching candidates to your job description</p>
            </div>

            {/* Content area */}
            <div className="p-6">
              {/* Progress section */}
              <div className="mb-6">
                <div className="flex items-center mb-4">
                  <div className="w-3 h-3 bg-[#08f2c3] rounded-full mr-3"></div>
                  <h4 className="text-lg font-medium text-gray-800">AI Recommendation in Progress</h4>
                </div>

                {/* Progress bar */}
                <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                  <div
                    className="bg-[#08f2c3] h-2 rounded-full transition-all duration-1000 ease-out"
                    style={{
                      width: `${progressInfo.percentage}%`,
                    }}
                  ></div>
                </div>

                {/* Progress message */}
                <div className="flex items-start space-x-2 mb-2">
                  <svg
                    className={`h-5 w-5 ${progressInfo.stage === 'Error' ? 'text-red-500' : 'text-[#08f2c3]'} mt-0.5 flex-shrink-0`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="text-sm text-gray-600">{progressInfo.message}</p>
                </div>

                {/* Current status text */}
                {progressInfo.stage !== 'Error' && progressInfo.stage !== 'Complete' && (
                    <p className="text-xs text-center text-gray-500 italic">{progressStatus}</p>
                )}
              </div>

              {/* Single heading for How Jumpstart Works */}
              <h3 className="text-xl font-medium text-gray-800 mb-4 text-center">How Jumpstart Works</h3>

              {/* Video section */}
              <div className="max-w-2xl mx-auto">
                <div className="rounded-lg overflow-hidden shadow-md">
                  <video
                    className="w-full h-full object-cover"
                    src={MAIN_VIDEO_PATH}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                  ></video>
                </div>
                <p className="text-sm text-gray-600 text-center mt-3">
                  Browse matched candidates, request interviews, and receive applications
                </p>
              </div>
            </div>
          </div>
        ) : (
          // ------------------------------
          // Success State – simplified pop‑up
          // ------------------------------
          <div className="flex flex-col p-8 text-center">
            {/* Celebration icon & headline */}
            <span className="text-4xl mb-4 block">🎉</span>
            <h3 className="text-2xl font-semibold">Role published!</h3>
            <p className="text-sm text-gray-600 mt-2">What would you like to do next?</p>

            {/* Action buttons */}
            <div className="flex flex-col-reverse sm:flex-row gap-3 justify-center mt-8 w-full sm:w-auto">
              <button
                onClick={handleBrowseClick}
                className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                Browse candidates
              </button>
              <button
                onClick={onConfirm}
                className="px-5 py-2 bg-[#08f2c3] text-black font-medium rounded-lg hover:bg-[#06c19c] transition-colors flex items-center justify-center shadow-sm"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                See AI matches
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecommendationModal;
